import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

/* =====================================================================
   AirbnbDatePicker — sélecteur de dates réutilisable style Airbnb.
   - mode="range"  : plage (début → fin), ex. séjour arrivée/départ.
   - mode="single" : date unique, ex. date de voyage.
   Zéro dépendance date : calculs en Date locale (pas d'UTC → pas d'off-by-one).
   value:
     - single : "YYYY-MM-DD" | ""
     - range  : { start: "YYYY-MM-DD"|"", end: "YYYY-MM-DD"|"" }
   onChange reçoit la même forme que value.
===================================================================== */

/* ---------- utils dates (locale, sans dépendance) ---------- */
const pad = (n) => String(n).padStart(2, "0");
export const toISO = (d) =>
  d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "";
export const fromISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isBeforeDay = (a, b) => atMidnight(a) < atMidnight(b);
const isBetween = (d, a, b) => atMidnight(d) > atMidnight(a) && atMidnight(d) < atMidnight(b);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const localeOf = (language) => (language === "ar" ? "ar-MA" : language === "en" ? "en-US" : "fr-FR");

/** Cellules d'un mois : padding lundi→dimanche + jours du mois. */
function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  // getDay: 0=dim..6=sam ; on veut la semaine commençant lundi.
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

/* ---------- Un mois de calendrier ---------- */
function MonthGrid({ year, month, language, minDate, selStart, selEnd, hoverEnd, mode, onPick, onHover }) {
  const cells = useMemo(() => monthMatrix(year, month), [year, month]);
  const monthLabel = new Date(year, month, 1).toLocaleDateString(localeOf(language), {
    month: "long",
    year: "numeric",
  });
  const weekdays = useMemo(() => {
    // Lundi de référence : 2024-01-01 est un lundi.
    const base = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) =>
      new Date(base.getFullYear(), base.getMonth(), base.getDate() + i).toLocaleDateString(localeOf(language), {
        weekday: "short",
      })
    );
  }, [language]);

  const rangeEnd = mode === "range" ? selEnd || hoverEnd : null;

  return (
    <div className="select-none">
      <div className="text-center font-bold text-gray-800 capitalize mb-3">{monthLabel}</div>
      <div className="grid grid-cols-7 gap-y-1 mb-1">
        {weekdays.map((w, i) => (
          <div key={i} className="text-center text-[11px] font-semibold text-gray-400 uppercase">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const disabled = minDate && isBeforeDay(day, minDate);
          const isStart = isSameDay(day, selStart);
          const isEnd = isSameDay(day, rangeEnd);
          const inRange = selStart && rangeEnd && isBetween(day, selStart, rangeEnd);
          const isSingle = mode === "single" && isSameDay(day, selStart);
          const highlighted = isStart || isEnd || isSingle;

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onMouseEnter={() => onHover && onHover(day)}
              onClick={() => onPick(day)}
              className={[
                "relative h-10 w-full flex items-center justify-center text-sm transition-colors",
                disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-orange-100",
                inRange ? "bg-orange-100 rounded-none" : "",
                isStart && rangeEnd ? "rounded-l-full" : "",
                isEnd ? "rounded-r-full" : "",
                highlighted ? "!bg-orange-600 !text-white font-bold rounded-full shadow-md" : "rounded-full",
              ].join(" ")}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Composant principal ---------- */
const AirbnbDatePicker = ({
  mode = "single",
  value,
  onChange,
  min,
  language = "fr",
  startLabel,
  endLabel,
  singleLabel,
  className = "",
  inline = false, // true = calendrier affiché directement (sans champ à cliquer)
}) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const seed =
      (mode === "range" ? fromISO(value?.start) : fromISO(value)) || new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });
  const [hoverDay, setHoverDay] = useState(null);
  const rootRef = useRef(null);
  const popRef = useRef(null);
  const [popStyle, setPopStyle] = useState({ top: 0, left: 0, width: 0 });

  const minDate = fromISO(min) || atMidnight(new Date());

  const selStart = mode === "range" ? fromISO(value?.start) : fromISO(value);
  const selEnd = mode === "range" ? fromISO(value?.end) : null;

  // Positionner le popover (portal) sous le champ, repositionne au scroll/resize.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 320), 680);
      let left = r.left;
      if (left + width > window.innerWidth - 12) left = Math.max(12, window.innerWidth - width - 12);
      setPopStyle({ top: r.bottom + 8, left, width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Fermer au clic extérieur / Échap.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const fmt = (d) =>
    d ? d.toLocaleDateString(localeOf(language), { day: "numeric", month: "short", year: "numeric" }) : "";

  const handlePick = (day) => {
    if (mode === "single") {
      onChange(toISO(day));
      setOpen(false);
      return;
    }
    // range
    if (!selStart || (selStart && selEnd)) {
      // (re)commence une nouvelle plage
      onChange({ start: toISO(day), end: "" });
      setHoverDay(null);
      return;
    }
    // selStart défini, selEnd vide → on fixe la fin
    if (isBeforeDay(day, selStart)) {
      onChange({ start: toISO(day), end: "" }); // clic avant le début → redémarre
      return;
    }
    if (isSameDay(day, selStart)) return; // même jour : on attend un vrai 2e jour
    onChange({ start: toISO(selStart), end: toISO(day) });
    setOpen(false);
  };

  // Un mois de calendrier décalé de `offset` mois par rapport à la vue courante.
  const renderMonth = (offset) => (
    <MonthGrid
      year={addMonths(viewDate, offset).getFullYear()}
      month={addMonths(viewDate, offset).getMonth()}
      language={language}
      minDate={minDate}
      selStart={selStart}
      selEnd={selEnd}
      hoverEnd={mode === "range" && selStart && !selEnd ? hoverDay : null}
      mode={mode}
      onPick={handlePick}
      onHover={mode === "range" && selStart && !selEnd ? setHoverDay : undefined}
    />
  );

  const navBar = (withClose) => (
    <div className="flex items-center justify-between mb-2">
      <button type="button" onClick={() => setViewDate((d) => addMonths(d, -1))} className="p-2 rounded-full hover:bg-gray-100 text-gray-600" aria-label="Mois précédent">
        <ChevronLeft className="w-5 h-5" />
      </button>
      {withClose && (
        <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 sm:hidden" aria-label="Fermer">
          <X className="w-5 h-5" />
        </button>
      )}
      <button type="button" onClick={() => setViewDate((d) => addMonths(d, 1))} className="p-2 rounded-full hover:bg-gray-100 text-gray-600" aria-label="Mois suivant">
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  // Récapitulatif de la plage (Arrivée → Départ) + bouton Effacer.
  const rangeSummary = mode === "range" && (
    <div className="flex items-center justify-between mb-3 px-1">
      <div className="flex items-center gap-2 text-sm">
        <span className={selStart ? "font-bold text-gray-900" : "text-gray-500"}>{selStart ? fmt(selStart) : startLabel || "Arrivée"}</span>
        <ChevronRight className="w-4 h-4 text-gray-300" />
        <span className={selEnd ? "font-bold text-gray-900" : "text-gray-500"}>{selEnd ? fmt(selEnd) : endLabel || "Départ"}</span>
      </div>
      {(selStart || selEnd) && (
        <button type="button" onClick={() => onChange({ start: "", end: "" })} className="text-xs font-semibold text-gray-500 hover:text-red-500 underline">
          Effacer
        </button>
      )}
    </div>
  );

  // Mode inline : le calendrier est affiché directement (aucun clic requis).
  if (inline) {
    return (
      <div className={`w-full ${className}`}>
        {rangeSummary}
        <div
          className="bg-white rounded-3xl border border-orange-100 shadow-sm p-3 sm:p-4"
          onMouseLeave={() => setHoverDay(null)}
        >
          {navBar(false)}
          {renderMonth(0)}
        </div>
      </div>
    );
  }

  const triggerText =
    mode === "range" ? (
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className={`truncate ${selStart ? "text-gray-900 font-semibold" : "text-gray-400"}`}>
          {selStart ? fmt(selStart) : startLabel || "Arrivée"}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-300 flex-none" />
        <span className={`truncate ${selEnd ? "text-gray-900 font-semibold" : "text-gray-400"}`}>
          {selEnd ? fmt(selEnd) : endLabel || "Départ"}
        </span>
      </div>
    ) : (
      <span className={`truncate flex-1 ${selStart ? "text-gray-900 font-semibold" : "text-gray-400"}`}>
        {selStart ? fmt(selStart) : singleLabel || "Choisir une date"}
      </span>
    );

  return (
    <div ref={rootRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 bg-white border-2 border-orange-200 hover:border-orange-400 rounded-2xl px-4 py-3.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300"
      >
        <CalendarIcon className="w-5 h-5 text-orange-500 flex-none" />
        {triggerText}
      </button>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                ref={popRef}
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                style={{ position: "fixed", top: popStyle.top, left: popStyle.left, width: popStyle.width, zIndex: 9999 }}
                className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-5"
              >
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={() => setViewDate((d) => addMonths(d, -1))}
                    disabled={isBeforeDay(addMonths(viewDate, -1), new Date(minDate.getFullYear(), minDate.getMonth(), 1)) && false}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                    aria-label="Mois précédent"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 sm:hidden"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewDate((d) => addMonths(d, 1))}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                    aria-label="Mois suivant"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-6"
                  onMouseLeave={() => setHoverDay(null)}
                >
                  <MonthGrid
                    year={viewDate.getFullYear()}
                    month={viewDate.getMonth()}
                    language={language}
                    minDate={minDate}
                    selStart={selStart}
                    selEnd={selEnd}
                    hoverEnd={mode === "range" && selStart && !selEnd ? hoverDay : null}
                    mode={mode}
                    onPick={handlePick}
                    onHover={mode === "range" && selStart && !selEnd ? setHoverDay : undefined}
                  />
                  <div className="hidden sm:block">
                    <MonthGrid
                      year={addMonths(viewDate, 1).getFullYear()}
                      month={addMonths(viewDate, 1).getMonth()}
                      language={language}
                      minDate={minDate}
                      selStart={selStart}
                      selEnd={selEnd}
                      hoverEnd={mode === "range" && selStart && !selEnd ? hoverDay : null}
                      mode={mode}
                      onPick={handlePick}
                      onHover={mode === "range" && selStart && !selEnd ? setHoverDay : undefined}
                    />
                  </div>
                </div>

                {mode === "range" && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => onChange({ start: "", end: "" })}
                      className="text-sm font-semibold text-gray-500 hover:text-gray-800 underline"
                    >
                      Effacer
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl"
                    >
                      Valider
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default AirbnbDatePicker;
